import { QueryClient, QueryCache } from "@tanstack/react-query";
import { message } from "antd";
import { get } from "lodash";

/*
 * Shared TanStack Query client for the whole app.
 *
 * Conventions:
 * - One file per domain under src/queries/, mirroring src/actions/.
 * - Each domain file exports both a hook (e.g. useLabels) and a queryOptions
 *   object (e.g. labelsQuery) so class code can call
 *   queryClient.ensureQueryData(labelsQuery) with the same key/fetcher.
 * - Query keys are arrays: ["labels"], ["users"], ["evaluations", id, "interests"].
 * - Opt into an error toast by setting meta.errorMessage on the query; the
 *   QueryCache onError below shows it. Queries that surface their own errors
 *   (or should fail silently) omit meta.errorMessage.
 */
const queryCache = new QueryCache({
  onError: (error, query) => {
    const errorMessage = get(query, "meta.errorMessage");
    if (errorMessage) {
      message.error(errorMessage);
    }
  },
});

export const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      // Matches current behavior; enable per-query where a screen benefits.
      refetchOnWindowFocus: false,
      // Never retry 4xx (a 401/403/404 won't fix itself); retry transient
      // network/5xx errors a couple of times.
      retry: (failureCount, error) => {
        const status = get(error, "response.status");
        if (status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/*
 * Shared preset for reference/lookup data (labels, users, entities, aggregates,
 * org phone numbers, …): rarely changes, so treat it as load-once-per-session.
 * Spread it into a query's options — do NOT move it into the global defaults
 * above, since most non-reference data (record details, lists) wants the
 * fresher defaults.
 *
 * - staleTime 24h caps worst-case staleness at a day (a self-heal for very
 *   long-lived tabs) instead of never-refetch.
 * - gcTime Infinity keeps it cached for the whole session, so revisiting a
 *   screen is always instant.
 *
 * Freshness after an edit must come from invalidating the query in its
 * mutation, not from staleness.
 */
export const REFERENCE_QUERY_OPTIONS = {
  staleTime: 24 * 60 * 60 * 1000,
  gcTime: Infinity,
};
